/**
 * ProductsCatalog — the /products catalog model (Mixtiles-style).
 *
 * A "product" is a named preset: a bundle count + frame style, shown as a card
 * with a carousel of VARIATIONS (grid shape × B&W). Tapping a variation opens the
 * scenery (WallProduct) preloaded with that config, in the scene the user picked.
 *
 * Card images are rendered live from <WallThumb/> for now; drop a static
 * AI-composited room shot per variation via `img` to override the live render.
 */
import { priceBundle, money } from "../WallProduct/data";

// Size tabs (Mixtiles: Small/Medium/Large) — map to frame-count tiers.
export const SIZES = [
  { id: "small", label: "קטן" },
  { id: "medium", label: "בינוני" },
  { id: "large", label: "גדול" },
];

// grid:'a'|'b' -> optional column override so a product can show two wall shapes.
// bw -> greyscale. scene -> default room for the preview (see env/manifest.json).
// Launch catalog: ONLY the focused 6-frame product, single variation. More products
// + variations are added back after this feature is tested (see wallproducts seed).
export const PRODUCTS = [
  {
    id: "village-6", name: "כפרי חמים", count: 6, frame: "village", size: "small",
    badge: "חדש", dims: "92 × 62 ס״מ", scene: "living",
    variations: [
      { grid: "a", cols: 3, bw: false, scene: "living" },
    ],
  },
  {
    id: "modern-styled-12", name: "גלריה מודרנית", count: 12, frame: "classic", size: "large",
    badge: "פופולארי ביותר", dims: "108 × 80 ס״מ", scene: "modern-styled",
    variations: [
      { grid: "a", cols: 4, bw: false, scene: "modern-styled" },
    ],
  },
  {
    id: "stone-16-diamond", name: "יהלום ירושלמי", count: 16, frame: "ever", size: "large",
    badge: "חדש", dims: "192 × 108 ס״מ", scene: "stone",
    // Diamond: rows of 1-2-3-4-3-2-1 = 16, on the Jerusalem-stone wall.
    variations: [
      { grid: "diamond", rows: [1, 2, 3, 4, 3, 2, 1], bw: false, scene: "stone" },
    ],
  },
  {
    id: "study-4", name: "פינת עבודה", count: 4, frame: "bold", size: "small",
    dims: "52 × 52 ס״מ", scene: "study",
    variations: [
      { grid: "a", cols: 2, bw: false, scene: "study" },
    ],
  },
  {
    id: "bedroom-calm-9", name: "רוגע", count: 9, frame: "classic", size: "medium",
    badge: "חדש", dims: "80 × 80 ס״מ", scene: "bedroom-calm",
    variations: [
      { grid: "a", cols: 3, bw: true, scene: "bedroom-calm" },
    ],
  },
  {
    id: "kids-blue-10", name: "חברים קטנים", count: 10, frame: "clean", size: "medium",
    badge: "חדש", dims: "136 × 52 ס״מ", scene: "kids-blue",
    variations: [
      { grid: "a", cols: 5, bw: false, scene: "kids-blue" },
    ],
  },
  {
    id: "coastal-10", name: "נוף הים", count: 10, frame: "nordic", size: "medium",
    badge: "חדש", dims: "136 × 52 ס״מ", scene: "coastal",
    variations: [
      { grid: "a", cols: 5, bw: false, scene: "coastal" }, // wide 5×2 band
    ],
  },
  {
    id: "entry-24", name: "גלריית הכניסה", count: 24, frame: "nordic", size: "large",
    badge: "חדש", dims: "164 × 108 ס״מ", scene: "entry-balatot",
    variations: [
      { grid: "a", cols: 6, bw: true, scene: "entry-balatot" }, // nordic B&W, single variation
    ],
  },
  {
    id: "music-studio-14", name: "קיר האולפן", count: 14, frame: "bold", size: "large",
    badge: "חדש", dims: "136 × 108 ס״מ", scene: "music-studio",
    variations: [
      { grid: "diamond", rows: [2, 3, 4, 3, 2], bw: false, scene: "music-studio" }, // records: 2·3·4·3·2 diamond
    ],
  },
  {
    id: "enterprise-lobby-40", name: "קיר הצוות", count: 40, frame: "ever", size: "large",
    badge: "לעסקים", dims: "234 × 136 ס״מ", scene: "enterprise-lobby",
    variations: [
      { grid: "brick", brick: [8, 8, 8, 8, 8], bw: false, scene: "enterprise-lobby" }, // team: staggered brick, 5 rows × 8, alt rows offset ½
    ],
  },
];

// Display price for a product card: "מ־₪X" plus struck-through regular price.
export function productPrice(count) {
  const p = priceBundle(count);
  return { from: money(p.final), was: p.savings > 0 ? money(p.regular) : null };
}

export const productsBySize = (sizeId) => PRODUCTS.filter((p) => p.size === sizeId);
